import { useEffect, useState } from 'react';

/**
 * The live inner width of an element, in CSS pixels.
 *
 * ## 🔴 Why a measurement and not another breakpoint
 *
 * S14's flat table has to choose how many columns to render, and the honest input is **how much room
 * the table actually has** — not how wide the window is. Measured in the real shell, those two come
 * apart badly:
 *
 * | viewport | scroller | 12 columns overflow by |
 * |---------:|---------:|-----------------------:|
 * |      721 |      599 |                  599px |
 * |     1024 |      870 |                  328px |
 * |     1279 |     1125 |                   73px |
 * | **1280** | **934**  |            **264px**   |
 * |     1494 |     1198 |                      0 |
 *
 * 🔴 **The budget goes DOWN as the window grows, at 1280.** `AppShell.COLLAPSE_BELOW_PX` expands the
 * sidebar from a 56px rail to 248px there, so the main column loses 192px in one pixel of window. A
 * `@media (min-width: …)` ladder cannot express that — it would have to *remove* columns as the
 * window widens, at a boundary that belongs to the sidebar rather than to the table. Measuring the
 * container gets it right without knowing the sidebar exists.
 *
 * ⚠ **`ResizeObserver`, not a resize listener.** The sidebar also collapses on a user click, which
 * changes this element's width with no window resize at all.
 *
 * ⚠ **Returns 0 until the first observation**, so a caller must treat 0 as *"not measured yet"* and
 * not as *"no room"* — otherwise the first paint renders the narrowest layout and then jumps.
 */
export function useElementWidth(element: HTMLElement | null): number {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!element) return;
    // Seed synchronously: the observer's first callback is a frame away, and without this the table
    // renders once at 0 before settling.
    setWidth(element.clientWidth);
    const observer = new ResizeObserver(() => {
      // `clientWidth` rather than the entry's `contentRect`: the entry reports the content box of the
      // observed element, and this one carries `overflow-x: auto` — so a horizontal scrollbar would
      // be included in one and not the other, which is exactly the 10px that makes a fit decision
      // oscillate between two column sets.
      setWidth((previous) => (previous === element.clientWidth ? previous : element.clientWidth));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return width;
}
